import {useMemo} from 'react';
import {
    arrayGetItemCyclic,
    colorBlend,
    colorIsDark,
    colorResolveLegibleForegroundColor,
    colorResolveMiddleColor,
    cssResolveRgbColorComponentsForFunctionalNotationFormat,
    evaluateFunction,
    materialPrimaryColorPaletteResolvePrimaryRelativeColorIndex,
} from '@wix/devzai-utils-common';
import {DoppeColorPaletteCalculatedColorType} from '../../../doppe-sdk';
import {
    useAssertDoppeViewerColorPaletteCalculatedColorsDataReactContext
} from '../../doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import {
    DopeStandardActionButtonColorMode,
    type DopeStandardActionButtonSkin,
    type DoppeStandardActionButtonHoverAnimation,
    type DoppeStandardActionButtonTemplate,
} from './doppe-standard-action-button-template.metadata';

/**
 * Resolved chrome values for a {@link DoppeStandardActionButtonTemplate}.
 * Each consumer maps these into its own Stylable CSS-vars object (since Stylable vars are
 * scoped per-file and cannot be shared across `.st.css` modules).
 */
export interface DoppeStandardActionButtonChromeStyle {
    roundness: number;
    skin: DopeStandardActionButtonSkin;
    hoverAnimation: DoppeStandardActionButtonHoverAnimation;
    buttonColor: string;
    mainColorRgb: string | null;
    blendedMainColor: string | null;
    gradientStart: string | null;
    gradientEnd: string | null;
    gradientLegibleForeground: string | null;
}

/**
 * Hook that resolves the visual chrome (color, roundness, skin, gradient) for a
 * {@link DoppeStandardActionButtonTemplate}. Used by both `DoppeStandardActionButton`
 * (page action buttons) and `DoppePageSocialLinks` (social-link buttons when their
 * Button Style is set to `Default` or `Custom`).
 *
 * Single source of truth for the color-cycling, blended-color and gradient-color logic.
 */
export function useDoppeStandardActionButtonChromeStyle(params: {
    templateProps: DoppeStandardActionButtonTemplate.TemplateProps;
    actionIndexInList: number | null | undefined;
}): DoppeStandardActionButtonChromeStyle {
    const {
        templateProps: {roundness, skin, hoverAnimation, colorMode},
        actionIndexInList,
    } = params;

    const colorPaletteCalculatedColorsData = useAssertDoppeViewerColorPaletteCalculatedColorsDataReactContext();

    const buttonColorTypes = useMemo(() => {
        switch (colorMode) {
            case DopeStandardActionButtonColorMode.Unified: {
                return [DoppeColorPaletteCalculatedColorType.MainColor];
            }
            case DopeStandardActionButtonColorMode.Alternate: {
                return [
                    DoppeColorPaletteCalculatedColorType.MainColor,
                    DoppeColorPaletteCalculatedColorType.MainColorBrightnessAlternating,
                ];
            }
            case DopeStandardActionButtonColorMode.Analogous: {
                return [
                    DoppeColorPaletteCalculatedColorType.MainColor,
                    DoppeColorPaletteCalculatedColorType.MainColorAnalogous,
                ];
            }
            case DopeStandardActionButtonColorMode.Triad: {
                return [
                    DoppeColorPaletteCalculatedColorType.MainColor,
                    DoppeColorPaletteCalculatedColorType.MainColorTriadic1,
                    DoppeColorPaletteCalculatedColorType.MainColorTriadic2,
                ];
            }
        }
    }, [colorMode]);

    const normalizedActionIndexInList = actionIndexInList ?? 0;
    const buttonColorType = arrayGetItemCyclic(buttonColorTypes, normalizedActionIndexInList);
    const buttonColorData = colorPaletteCalculatedColorsData[buttonColorType];

    const mainColor = buttonColorData.color;

    const mainColorRgb = useMemo(
        () => cssResolveRgbColorComponentsForFunctionalNotationFormat(mainColor),
        [mainColor]
    );

    const blendedMainColor = useMemo(() => {
        return colorIsDark(mainColor) ? colorBlend('#fff', mainColor, 0.2) : colorBlend('#000', mainColor, 0.1);
    }, [mainColor]);

    const gradientSkinColors = evaluateFunction(() => {
        const mainButtonColorType = buttonColorTypes[0];
        const mainButtonColorData = colorPaletteCalculatedColorsData[mainButtonColorType];
        const mainButtonColorMaterialPrimaryColorPalette = mainButtonColorData.materialPrimaryColorPalette;

        if (mainButtonColorMaterialPrimaryColorPalette) {
            const mainButtonColorFirstColorPaletteIndex = mainButtonColorMaterialPrimaryColorPalette.primaryColorIndex;
            const mainButtonColorSecondColorPaletteIndex = materialPrimaryColorPaletteResolvePrimaryRelativeColorIndex(
                mainButtonColorMaterialPrimaryColorPalette,
                4
            );

            const mainButtonColorGradientStart = mainButtonColorMaterialPrimaryColorPalette.palette[mainButtonColorFirstColorPaletteIndex];
            const mainButtonColorGradientEnd = mainButtonColorMaterialPrimaryColorPalette.palette[mainButtonColorSecondColorPaletteIndex];

            const currentButtonColorMaterialPrimaryColorPalette =
                buttonColorData.materialPrimaryColorPalette ?? mainButtonColorMaterialPrimaryColorPalette;

            const gradientStart = currentButtonColorMaterialPrimaryColorPalette.palette[currentButtonColorMaterialPrimaryColorPalette.primaryColorIndex];
            const gradientEnd = currentButtonColorMaterialPrimaryColorPalette.palette[mainButtonColorSecondColorPaletteIndex];

            const middleColor = colorResolveMiddleColor(mainButtonColorGradientStart, mainButtonColorGradientEnd);

            return {
                gradientStart: gradientStart,
                gradientEnd: gradientEnd,
                gradientLegibleForeground: middleColor ? colorResolveLegibleForegroundColor(middleColor) : null,
            };
        } else {
            return null;
        }
    });

    return {
        roundness,
        skin,
        hoverAnimation,
        buttonColor: mainColor,
        mainColorRgb,
        blendedMainColor,
        gradientStart: gradientSkinColors?.gradientStart ?? null,
        gradientEnd: gradientSkinColors?.gradientEnd ?? null,
        gradientLegibleForeground: gradientSkinColors?.gradientLegibleForeground ?? null,
    };
}
